import React from 'react';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import './PlayerCard.css';

function PlayerCard({
  player,
  meritRank,
  paceRank,
  hasConflict = false,
  isDragging = false,
  isInOverflow = false,
  playerFeedback = null,
  onFeedbackClick = null,
  partnerFulfillment = null,  // { requests: [{ targetGhin, targetName, honored }] }
  timeFulfillment = null       // { type, preference, honored, assignedTime, constraint }
}) {
  const cardClasses = cn(
    'player-card',
    hasConflict && 'has-conflict',
    isDragging && 'dragging',
    isInOverflow && 'in-overflow',
    player.manualOverride?.isOverridden && 'manual-override'
  );

  const getTimePreferenceIcon = (preference) => {
    switch (preference) {
      case 'early': return '🌅';
      case 'late': return '🌙';
      default: return '';
    }
  };

  const getPaceVariant = (rank) => {
    if (rank === 'N/A') return 'secondary';
    const numRank = parseInt(rank);
    if (numRank <= 100) return 'success';
    if (numRank <= 300) return 'warning';
    return 'destructive';
  };

  const getMeritVariant = (rank) => {
    if (rank === 'N/A') return 'secondary';
    const numRank = parseInt(rank);
    if (numRank <= 50) return 'default';
    if (numRank <= 150) return 'info';
    if (numRank <= 300) return 'secondary';
    return 'outline';
  };

  const formatHandicap = (handicap) => {
    if (typeof handicap === 'number') {
      return handicap.toFixed(1);
    }
    return handicap;
  };

  // Check if player has any requests
  const hasRequests = (player.timeRequests && player.timeRequests.length > 0) ||
                     (player.partnerRequests && player.partnerRequests.length > 0) ||
                     (player.teamRequests && player.teamRequests.length > 0) ||
                     (player.parsedTimePreference && player.parsedTimePreference.trim()) ||
                     (player.parsedPartnerPreference && player.parsedPartnerPreference.trim()) ||
                     (player.parsedPairingPreference && player.parsedPairingPreference.trim()) ||
                     (player.originalPairingRequest && player.originalPairingRequest.trim()) ||
                     (player.rawPartnerRequest && player.rawPartnerRequest.trim());

  return (
    <div className={cardClasses}>
      <div className="player-main-info">
        <div className="player-name-section">
          <h4 className="player-name">
            {player.name}
            {hasRequests && <span className="has-requests-indicator" title="Has tournament requests">📋</span>}
          </h4>
          <span className="player-ghin">{player.ghin}</span>
          {player.timePreference !== 'none' && (
            <span className="time-preference" title={`Prefers ${player.timePreference} times`}>
              {getTimePreferenceIcon(player.timePreference)}
            </span>
          )}
        </div>

        <div className="handicap-section">
          <span className="handicap-label">HI</span>
          <span className="handicap-value">{formatHandicap(player.handicapIndex)}</span>
        </div>
      </div>

      <div className="player-stats">
        <Badge variant={getMeritVariant(meritRank)} className="stat-badge">
          <span className="stat-label">Merit</span>
          <span className="stat-value">{meritRank}</span>
        </Badge>

        <Badge variant={getPaceVariant(paceRank)} className="stat-badge">
          <span className="stat-label">Pace</span>
          <span className="stat-value">{paceRank}</span>
        </Badge>
      </div>

      {/* Direct Request Display - Shows requests as readable text */}
      <div className="player-requests-direct">
        {/* Partner Requests - Show names directly */}
        {player.partnerRequests && player.partnerRequests.length > 0 && (
          <div className="request-row partner-request-row">
            <span className="request-icon">👤</span>
            <span className="request-text">
              {player.partnerRequests.map((req, i) => {
                const names = req.targetNames?.join(', ') || req.targetName || 'Unknown';
                const prefix = req.type === 'avoid' ? 'Avoid: ' : '';
                return (
                  <span key={i} className={`request-name ${req.type === 'avoid' ? 'avoid-request' : ''}`}>
                    {i > 0 && ', '}{prefix}{names}
                  </span>
                );
              })}
            </span>
          </div>
        )}

        {/* Time Requests - Show preference directly */}
        {player.timeRequests && player.timeRequests.length > 0 && (
          <div className="request-row time-request-row">
            <span className="request-icon">{player.timeRequests[0].isHard ? '⏰' : '🕐'}</span>
            <span className="request-text">
              {player.timeRequests.map((req, i) => {
                let text = req.type;
                if (req.value) text = req.value;
                else if (req.type === 'early') text = 'Early';
                else if (req.type === 'late') text = 'Late';
                else if (req.type === 'before') text = `Before ${req.value || ''}`;
                else if (req.type === 'after') text = `After ${req.value || ''}`;
                return (
                  <span key={i} className={`request-time ${req.isHard ? 'hard-time' : ''}`}>
                    {i > 0 && ', '}{text}
                  </span>
                );
              })}
            </span>
          </div>
        )}

        {/* Fallback: Parsed text preferences if no structured requests */}
        {(!player.partnerRequests || player.partnerRequests.length === 0) &&
         (player.parsedPartnerPreference?.trim() || player.parsedPairingPreference?.trim()) && (
          <div className="request-row partner-request-row parsed">
            <span className="request-icon">👤</span>
            <span className="request-text">
              {player.parsedPartnerPreference?.trim() || player.parsedPairingPreference?.trim()}
            </span>
          </div>
        )}

        {(!player.timeRequests || player.timeRequests.length === 0) &&
         player.parsedTimePreference && player.parsedTimePreference.trim() && (
          <div className="request-row time-request-row parsed">
            <span className="request-icon">🕐</span>
            <span className="request-text">{player.parsedTimePreference}</span>
          </div>
        )}

        {/* Original/raw request fallback */}
        {(!player.partnerRequests || player.partnerRequests.length === 0) &&
         !player.parsedPartnerPreference?.trim() &&
         !player.parsedPairingPreference?.trim() &&
         (player.originalPairingRequest?.trim() || player.rawPartnerRequest?.trim()) && (
          <div className="request-row partner-request-row raw">
            <span className="request-icon">📝</span>
            <span className="request-text raw-text">
              {player.originalPairingRequest?.trim() || player.rawPartnerRequest?.trim()}
            </span>
          </div>
        )}

        {/* Review indicator */}
        {player.needsReview && (
          <div className="request-row review-row">
            <span className="request-icon">⚠️</span>
            <span className="request-text review-text">Needs review</span>
          </div>
        )}
      </div>

      {/* Partner Fulfillment Display - Shows who this player requested and if honored */}
      {partnerFulfillment && partnerFulfillment.requests && partnerFulfillment.requests.length > 0 && (
        <div className="fulfillment-section partner-fulfillment">
          <span className="fulfillment-label">Wants:</span>
          <div className="fulfillment-items">
            {partnerFulfillment.requests.map((request, index) => (
              <span
                key={index}
                className={`fulfillment-item ${request.honored ? 'honored' : 'not-honored'}`}
                title={request.honored ? 'Partner request fulfilled' : 'Partner request not fulfilled'}
              >
                {request.targetName}
                <span className="fulfillment-indicator">
                  {request.honored ? '✓' : '✗'}
                </span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Time Fulfillment Display - Shows time constraint/preference and if honored */}
      {timeFulfillment && (timeFulfillment.constraint || timeFulfillment.preference) && (
        <div className="fulfillment-section time-fulfillment">
          <span className="fulfillment-label">
            {timeFulfillment.type === 'hard' ? '⏰' : '🕐'}
          </span>
          <span
            className={`fulfillment-item ${timeFulfillment.honored ? 'honored' : 'not-honored'} ${timeFulfillment.type === 'hard' ? 'hard-constraint' : 'soft-preference'}`}
            title={`${timeFulfillment.type === 'hard' ? 'Hard constraint' : 'Soft preference'}: ${timeFulfillment.constraint || timeFulfillment.preference}${timeFulfillment.assignedTime ? ` - Assigned: ${timeFulfillment.assignedTime}` : ''}`}
          >
            {timeFulfillment.constraint || timeFulfillment.preference}
            <span className="fulfillment-indicator">
              {timeFulfillment.honored ? '✓' : '✗'}
            </span>
          </span>
        </div>
      )}

      {/* Feedback Badge */}
      {playerFeedback && playerFeedback.totalCount > 0 && (
        <div
          className="feedback-section"
          onClick={() => onFeedbackClick && onFeedbackClick(player.ghin)}
          title={`${playerFeedback.totalCount} feedback comments`}
        >
          <div className="feedback-badge-container">
            <Badge
              variant={playerFeedback.pendingCount > 0 ? 'warning' : 'success'}
              className="feedback-badge"
            >
              <span className="feedback-icon">💬</span>
              <span className="feedback-count">{playerFeedback.totalCount}</span>
            </Badge>

            {/* Status breakdown */}
            <div className="feedback-status-breakdown">
              {playerFeedback.pendingCount > 0 && (
                <Badge variant="warning" className="status-indicator" title={`${playerFeedback.pendingCount} pending`}>
                  {playerFeedback.pendingCount}
                </Badge>
              )}
              {playerFeedback.addressedCount > 0 && (
                <Badge variant="success" className="status-indicator" title={`${playerFeedback.addressedCount} addressed`}>
                  {playerFeedback.addressedCount}
                </Badge>
              )}
              {playerFeedback.ignoredCount > 0 && (
                <Badge variant="secondary" className="status-indicator" title={`${playerFeedback.ignoredCount} ignored`}>
                  {playerFeedback.ignoredCount}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {player.manualOverride?.isOverridden && (
        <div className="manual-override-indicator" title="Manually placed">
          <Badge variant="default" className="override-badge">📌</Badge>
        </div>
      )}

      {hasConflict && (
        <div className="conflict-indicator" title="Has conflicts">
          <Badge variant="destructive" className="conflict-badge">⚠️</Badge>
        </div>
      )}
    </div>
  );
}

export default PlayerCard;
